import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { SHIFT_TYPES } from '../../../data/nurseAppModels';

const ShiftDetailModal = ({
  isOpen,
  onClose,
  date,
  plan,
  teamMembersWithProfile = []
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [originalShifts, setOriginalShifts] = useState([]);
  const [swappedShifts, setSwappedShifts] = useState([]);
  const [swapRequests, setSwapRequests] = useState([]);
  const [allSwaps, setAllSwaps] = useState([]);

  // Fetch shift details for the selected date
  useEffect(() => {
    const fetchShiftDetails = async () => {
      if (!isOpen || !date || !plan || plan.status !== 'APPROVED') {
        setOriginalShifts([]);
        setSwappedShifts([]);
        setSwapRequests([]);
        return;
      }

      try {
        setLoading(true);
        const dateKey = date.toISOString().split('T')[0];
        const planDocId = plan.id; // Use plan document ID
        
        // Calculate start and end of day
        const startOfDay = new Date(date);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(date);
        endOfDay.setHours(23, 59, 59, 999);

        // Fetch all shifts for this date and plan
        const shiftsSnapshot = await getDocs(
          query(
            collection(db, 'shifts'),
            where('planId', '==', planDocId)
          )
        );

        // Filter shifts for this date
        const shiftsForDate = shiftsSnapshot.docs
          .map(docSnap => ({
            id: docSnap.id,
            ...docSnap.data()
          }))
          .filter(shift => {
            const shiftDate = shift.startTime?.toDate 
              ? shift.startTime.toDate()
              : shift.startTime?.seconds 
              ? new Date(shift.startTime.seconds * 1000)
              : null;
            if (!shiftDate) return false;
            const shiftDateStr = shiftDate.toISOString().split('T')[0];
            return shiftDateStr === dateKey;
          });

        // Fetch all swaps for this plan
        const swapsSnapshot = await getDocs(
          query(
            collection(db, 'shiftSwaps'),
            where('planId', '==', planDocId)
          )
        );

        const allSwapsData = swapsSnapshot.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data()
        }));

        setAllSwaps(allSwapsData);

        // Separate original and swapped shifts
        const original = [];
        const swapped = [];
        const requests = [];

        shiftsForDate.forEach(shift => {
          // Check if this shift was swapped
          const swap = allSwapsData.find(s => {
            // For plan-based swaps
            if (s.originalShift?.date === dateKey && 
                s.originalShift?.shiftType === shift.shiftType &&
                s.status === 'APPROVED') {
              // Check if assignedTo changed
              return s.originalShift.userId !== shift.assignedTo;
            }
            // For shift-based swaps
            if (s.originalShiftId === shift.id && s.status === 'APPROVED') {
              return true;
            }
            return false;
          });

          if (swap) {
            swapped.push({
              ...shift,
              isOriginal: false,
              swappedFrom: {
                userId: swap.originalShift?.userId || swap.requestedBy,
                userName: swap.originalShift?.userName || swap.requestedByName
              },
              swapId: swap.id,
              swapStatus: swap.status
            });
          } else {
            original.push({
              ...shift,
              isOriginal: true
            });
          }
        });

        // Get swap requests for this date (PENDING or MEMBER_APPROVED)
        const dateSwapRequests = allSwapsData.filter(swap => {
          if (swap.status === 'PENDING' || swap.status === 'MEMBER_APPROVED') {
            // Check if swap is for this date
            if (swap.originalShift?.date === dateKey) {
              return true;
            }
            // For shift-based swaps, check startTime
            if (swap.originalShift?.startTime) {
              const swapDate = swap.originalShift.startTime.toDate 
                ? swap.originalShift.startTime.toDate().toISOString().split('T')[0]
                : swap.originalShift.startTime.seconds 
                ? new Date(swap.originalShift.startTime.seconds * 1000).toISOString().split('T')[0]
                : null;
              return swapDate === dateKey;
            }
          }
          return false;
        });

        // Fetch user data for swap requests
        const requestsWithUsers = await Promise.all(
          dateSwapRequests.map(async (swap) => {
            const [requestedByUser, requestedToUser] = await Promise.all([
              getDoc(doc(db, 'users', swap.requestedBy)),
              getDoc(doc(db, 'users', swap.requestedTo))
            ]);

            return {
              ...swap,
              requestedByUser: requestedByUser.exists() ? requestedByUser.data() : null,
              requestedToUser: requestedToUser.exists() ? requestedToUser.data() : null
            };
          })
        );

        setOriginalShifts(original);
        setSwappedShifts(swapped);
        setSwapRequests(requestsWithUsers);
      } catch (err) {
        console.error('Error fetching shift details:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchShiftDetails();
  }, [isOpen, date, plan]);

  // Get shift type color
  const getShiftTypeColor = (shiftType) => {
    switch (shiftType) {
      case SHIFT_TYPES.MORNING:
        return 'bg-green-500';
      case SHIFT_TYPES.AFTERNOON:
        return 'bg-yellow-500';
      case SHIFT_TYPES.NIGHT:
        return 'bg-blue-500';
      default:
        return 'bg-gray-500';
    }
  };

  // Get shift type icon
  const getShiftTypeIcon = (shiftType) => {
    switch (shiftType) {
      case SHIFT_TYPES.MORNING:
        return '☀️';
      case SHIFT_TYPES.AFTERNOON:
        return '🌤️';
      case SHIFT_TYPES.NIGHT:
        return '🌙';
      default:
        return '';
    }
  };

  // Get shift type label
  const getShiftTypeLabel = (shiftType) => {
    switch (shiftType) {
      case SHIFT_TYPES.MORNING:
        return 'เช้า';
      case SHIFT_TYPES.AFTERNOON:
        return 'บ่าย';
      case SHIFT_TYPES.NIGHT:
        return 'ดึก';
      default:
        return shiftType;
    }
  };

  // Get user display name
  const getUserDisplayName = (userId) => {
    const member = teamMembersWithProfile.find(m => m.uid === userId);
    return member?.displayName || member?.name || 'Unknown';
  };

  // Get user photo
  const getUserPhoto = (userId) => {
    const member = teamMembersWithProfile.find(m => m.uid === userId);
    return member?.photoURL || null;
  };

  // Format time
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : 
                 timestamp.seconds ? new Date(timestamp.seconds * 1000) : null;
    if (!date) return '';
    return date.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' });
  };

  // Format date
  const formatDate = (date) => {
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'PENDING':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs">รอตอบรับ</span>;
      case 'MEMBER_APPROVED':
        return <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs">รอหัวหน้าอนุมัติ</span>;
      case 'APPROVED':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">อนุมัติแล้ว</span>;
      case 'REJECTED':
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">ปฏิเสธ</span>;
      default:
        return null;
    }
  };

  if (!isOpen || !date) return null;

  const totalShifts = originalShifts.length + swappedShifts.length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 my-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">
              รายละเอียดเวร
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              วันที่ {formatDate(date)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Summary */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-gray-800">{originalShifts.length}</p>
              <p className="text-xs text-gray-600">แผนเดิม</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">{swappedShifts.length}</p>
              <p className="text-xs text-gray-600">ปรับเปลี่ยน</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-600">{swapRequests.length}</p>
              <p className="text-xs text-gray-600">คำขอแลกเวร</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-sm text-gray-500 mt-2">กำลังโหลดข้อมูล...</p>
          </div>
        ) : (
          <>
            {/* Original Shifts */}
            {originalShifts.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">✅</span>
                  <h4 className="font-semibold text-gray-800">แผนเดิม</h4>
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                    {originalShifts.length} เวร
                  </span>
                </div>
                <div className="space-y-3">
                  {originalShifts.map((shift) => {
                    const userPhoto = getUserPhoto(shift.assignedTo);
                    const userName = getUserDisplayName(shift.assignedTo);
                    
                    return (
                      <div
                        key={shift.id}
                        className="bg-white border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-start gap-3">
                          <div className={`${getShiftTypeColor(shift.shiftType)} text-white w-12 h-12 rounded-lg flex items-center justify-center text-xl flex-shrink-0`}>
                            {getShiftTypeIcon(shift.shiftType)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-gray-800">
                                {getShiftTypeLabel(shift.shiftType)}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                              {userPhoto ? (
                                <img
                                  src={userPhoto}
                                  alt={userName}
                                  className="w-6 h-6 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-xs font-semibold text-gray-700">
                                  {userName?.charAt(0) || '?'}
                                </div>
                              )}
                              <span className="text-sm text-gray-700">{userName}</span>
                            </div>
                            {shift.startTime && shift.endTime && (
                              <p className="text-xs text-gray-500">
                                🕐 {formatTime(shift.startTime)} - {formatTime(shift.endTime)}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Swapped Shifts */}
            {swappedShifts.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">🔄</span>
                  <h4 className="font-semibold text-gray-800">ปรับเปลี่ยน (จาก Swap)</h4>
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                    {swappedShifts.length} เวร
                  </span>
                </div>
                <div className="space-y-3">
                  {swappedShifts.map((shift) => {
                    const userPhoto = getUserPhoto(shift.assignedTo);
                    const userName = getUserDisplayName(shift.assignedTo);
                    const swappedFromName = getUserDisplayName(shift.swappedFrom?.userId);
                    
                    return (
                      <div
                        key={shift.id}
                        className="bg-blue-50 border border-blue-300 rounded-lg p-4"
                      >
                        <div className="flex items-start gap-3">
                          <div className={`${getShiftTypeColor(shift.shiftType)} text-white w-12 h-12 rounded-lg flex items-center justify-center text-xl flex-shrink-0`}>
                            {getShiftTypeIcon(shift.shiftType)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-gray-800">
                                {getShiftTypeLabel(shift.shiftType)}
                              </span>
                              {getStatusBadge(shift.swapStatus)}
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                              {userPhoto ? (
                                <img
                                  src={userPhoto}
                                  alt={userName}
                                  className="w-6 h-6 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-6 h-6 rounded-full bg-blue-300 flex items-center justify-center text-xs font-semibold text-blue-700">
                                  {userName?.charAt(0) || '?'}
                                </div>
                              )}
                              <span className="text-sm text-gray-700">{userName}</span>
                            </div>
                            <div className="bg-white rounded p-2 mb-2">
                              <p className="text-xs text-gray-600">
                                🔄 แทนที่: <span className="font-medium">{swappedFromName}</span>
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                Swap #{shift.swapId?.substring(0, 8)} ({shift.swapStatus})
                              </p>
                            </div>
                            {shift.startTime && shift.endTime && (
                              <p className="text-xs text-gray-500">
                                🕐 {formatTime(shift.startTime)} - {formatTime(shift.endTime)}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Swap Requests */}
            {swapRequests.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">🔄</span>
                  <h4 className="font-semibold text-gray-800">คำขอแลกเวรที่เกี่ยวข้อง</h4>
                  <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs">
                    {swapRequests.length} คำขอ
                  </span>
                </div>
                <div className="space-y-3">
                  {swapRequests.map((swap) => {
                    const requesterName = swap.requestedByUser?.displayName || swap.requestedByName || 'Unknown';
                    const requestedToName = swap.requestedToUser?.displayName || swap.requestedToName || 'Unknown';
                    
                    return (
                      <div
                        key={swap.id}
                        className={`border rounded-lg p-4 ${
                          swap.status === 'PENDING' 
                            ? 'bg-yellow-50 border-yellow-200'
                            : swap.status === 'MEMBER_APPROVED'
                            ? 'bg-orange-50 border-orange-200'
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-gray-800">
                                {requesterName} ↔ {requestedToName}
                              </span>
                              {getStatusBadge(swap.status)}
                            </div>
                            {swap.originalShift?.shiftType && (
                              <p className="text-sm text-gray-600 mb-1">
                                {getShiftTypeIcon(swap.originalShift.shiftType)} {getShiftTypeLabel(swap.originalShift.shiftType)}
                              </p>
                            )}
                            {swap.message && (
                              <p className="text-sm text-gray-700 mt-2 italic">
                                "{swap.message}"
                              </p>
                            )}
                            {swap.createdAt && (
                              <p className="text-xs text-gray-500 mt-2">
                                ส่งเมื่อ: {swap.createdAt.toDate ? 
                                  swap.createdAt.toDate().toLocaleDateString('th-TH', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  }) : ''}
                              </p>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            navigate(`/nurse/shifts/swaps/history`);
                            onClose();
                          }}
                          className="mt-2 text-xs text-blue-600 hover:text-blue-700 underline"
                        >
                          ดูรายละเอียด →
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Empty State */}
            {totalShifts === 0 && swapRequests.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500">ไม่มีเวรในวันนี้</p>
              </div>
            )}
          </>
        )}

        {/* Close Button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
          >
            ปิด
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShiftDetailModal;

